import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drawer, Form, Button, Select, Input, Layout, message } from "antd";
import { validateMessages } from "utils/misc";
import { actionForAddSowAbbr } from "services/redux/officeresource/sowabbr/action"; // Add action
import { actionForUpdateSowAbbr } from "services/redux/officeresource/sowabbr/action"; // Update action
import { getAllUsers } from "services/remanentCalls";
import axios from "axios";
import PropTypes from "prop-types"; // For prop validation
import { appBaseUrl } from "utils/config";

const { Header } = Layout;
const { Option } = Select;

const CityAddUpdateDrawer = ({
  onAddUpdateCountryClose,
  drawerVisibility,
  isUpdateCountry, // This prop should contain the data of the item being edited
  selectedRecord,
}) => {
  const { sowAbbrAddResponse, sowAbbrUpdateResponse } = useSelector(
    (state) => state.sowAbbrReducer
  );
  // console.log("drawerVisibility:", drawerVisibility, onAddUpdateCountryClose);
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [scopeTags, setScopeTags] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    // console.log("selectedRecord in useEffect:", selectedRecord); // Debugging

    if (!isUpdateCountry) {
      form.resetFields();
      setSelectedKeywords([]); // Clear selected keywords for new entries
    } else {
      // Populate form fields with the data from `isUpdateCountry`
      form.setFieldsValue({
        abbr: selectedRecord?.abbr,
        expandedText: selectedRecord?.expandedText,
      });
      setSelectedKeywords(
        Array.isArray(selectedRecord?.expandedText)
          ? selectedRecord?.expandedText?.flat()
          : []
      );
    }
  }, [selectedRecord]);

  // Handle success messages after adding or updating
  useEffect(() => {
    if (sowAbbrAddResponse) {
      message.success("SowAbbr created successfully!");
      handleClose();
    }
    if (sowAbbrUpdateResponse) {
      message.success("SowAbbr updated successfully!");
      handleClose();
    }
  }, [sowAbbrAddResponse, sowAbbrUpdateResponse]);

  // Fetch users and scope tags on component mount
  useEffect(() => {
    fetchAllUsers();
    fetchScopeTags();
  }, []);

  // Fetch all users
  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const resp = await getAllUsers();
      if (resp && resp.status === 200) {
        setUsers(resp.data);
      }
    } catch (err) {
      // console.error("Failed to fetch users:", err);
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  // Fetch scope tags from the API
  const fetchScopeTags = async () => {
    try {
      const response = await axios.get(
        `${appBaseUrl}/referenceManagement/sowAbbrList`
      );
      if (response.status === 200) {
        const formattedTags = response.data.map((item) => ({
          id: item.id,
          name: item.expandedText,
        }));
        setScopeTags(formattedTags);
      }
    } catch (error) {
      // console.error("Failed to fetch Keywords for Retrieval:", error);
      message.error("Failed to fetch Keywords for Retrieval");
    }
  };

  // Handle drawer close
  const handleClose = () => {
    form.resetFields();
    setSelectedKeywords([]);
    onAddUpdateCountryClose();
  };

  // Handle form submission
  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const body = {
        ...values,
        expandedText: selectedKeywords, // Send as a flat array
      };

      if (selectedRecord && selectedRecord.id) {
        dispatch(actionForUpdateSowAbbr({ ...body, id: selectedRecord.id }));
      } else {
        dispatch(actionForAddSowAbbr(body));
      }

      onAddUpdateCountryClose();
    } catch (errorInfo) {
      // console.error("Form Validation Error:", errorInfo);
      message.error("Validation failed. Please check the form.");
    }
  };

  // Handle keyword selection change
  const handleSelectChange = (values) => {
    setSelectedKeywords(values.flat()); // Flatten the array
  };

  return (
    <Drawer
      visible={drawerVisibility}
      width={472}
      maskClosable={false}
      className="drawer"
      onClose={handleClose}
      closable={false}
    >
      <Header className="drawer__header">
        <h4 className="drawer__header--title">
          {`${isUpdateCountry ? "Update " : "Create"} SowAbbr`}
        </h4>
      </Header>
      <div>
        <Form
          className="drawer-form-wrapper"
          layout="vertical"
          form={form}
          name="control-hooks"
          validateMessages={validateMessages}
        >
          <Form.Item
            name="abbr"
            label="SowAbbr Title"
            rules={[{ required: true, message: "Please enter SowAbbr Title" }]}
            style={{ marginBottom: "6px", padding: "6px" }}
          >
            <Input autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="expandedText"
            label="Keywords for Retrieval"
            style={{ marginBottom: "10px", padding: "7px" }}
          >
            <Select
              // placeholder="Select Keywords for Retrieval"
              mode="multiple"
              style={{ width: "100%" }}
              listHeight={100}
              onChange={handleSelectChange}
              value={selectedKeywords} // Controlled value
              loading={loading}
            >
              {scopeTags.map((tag) => (
                <Option value={tag.name} key={tag.id} style={{ margin: "5px" }}>
                  {tag.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedKeywords.length > 0 && (
            <div
              style={{
                marginTop: "10px",
                padding: "10px",
                background: "#f5f5f5",
                borderRadius: "5px",
              }}
            >
              <strong>Selected Keywords:</strong>
              <ul
                style={{
                  paddingLeft: "20px",
                  marginTop: "5px",
                  listStyleType: "disc",
                }}
              >
                {selectedKeywords.map((keyword, index) => (
                  <li key={index} style={{ marginBottom: "5px" }}>
                    {keyword}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Form>
      </div>
      <div className="model-footer">
        <Button onClick={handleClose} aria-label="Cancel">
          Cancel
        </Button>
        <Button onClick={onSubmit} type="primary" loading={loading}>
          {isUpdateCountry ? "Save Changes" : "Add"}
        </Button>
      </div>
    </Drawer>
  );
};

// Prop validation
CityAddUpdateDrawer.propTypes = {
  onAddUpdateCountryClose: PropTypes.func.isRequired,
  drawerVisibility: PropTypes.bool.isRequired,
  isUpdateCountry: PropTypes.shape({
    id: PropTypes.number,
    abbr: PropTypes.string,
    expandedText: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
};

export default CityAddUpdateDrawer;
